import { Button } from '@mui/material';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import ModalScaffold from '@/components/Scaffolds/ModalScaffold/ModalScaffold';
import type { NotificationMessageDto } from '@/models/VnMessageService';
import { isNotificationLiveBySendDate } from '@/models/VnMessageService';
import { NotificationDisplay } from '@/pages/VNMS/shared/components/NotificationBody';
import { VnMessageService } from '@/services/VnMessageService';
import { applicationDataSelector } from '@/store/slices/application-data';

type NotificationMode = 'live' | 'preview';
type NotificationCenterContextValue = {
  notifications: NotificationMessageDto[];
  unreadCount: number;
  readIds: string[];
  loading: boolean;
  refresh: () => Promise<{ notifications: NotificationMessageDto[]; readIds: string[] }>;
  markAsRead: (notificationId: string) => void;
  markAsUnread: (notificationId: string) => void;
  preview: (notification: NotificationMessageDto) => void;
};
type Props = {
  children: React.ReactNode;
};

const NotificationCenterContext = createContext<NotificationCenterContextValue>({
  notifications: [],
  unreadCount: 0,
  readIds: [],
  loading: false,
  refresh: async () => ({ notifications: [], readIds: [] }),
  markAsRead: () => {},
  markAsUnread: () => {},
  preview: () => {},
});

export const useNotificationCenter = () => useContext(NotificationCenterContext);

export const NotificationCenterProvider = ({ children }: Props) => {
  const { ouData } = useSelector(applicationDataSelector);
  const ouId: string | undefined = ouData?.ouId;

  const [mode, setMode] = useState<NotificationMode>('live');
  const [notifications, setNotifications] = useState<NotificationMessageDto[]>([]);
  const [takeoverQueue, setTakeoverQueue] = useState<NotificationMessageDto[]>([]);
  const [preview, setPreview] = useState<NotificationMessageDto[]>([]);
  const [readIds, setReadIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  /** Session-only: ids deferred via "Remind me later" so refresh does not re-queue takeovers. */
  const deferredTakeoverIdsRef = useRef<Set<string>>(new Set());
  const lastOuIdRef = useRef<string | undefined>(undefined);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !readIds.includes(notification.id)).length,
    [notifications, readIds]
  );

  const markAsRead = useCallback((notificationId: string) => {
    VnMessageService.markAsRead(notificationId);
    setReadIds((prev) => (prev.includes(notificationId) ? prev : [...prev, notificationId]));
  }, []);

  const markAsUnread = useCallback((notificationId: string) => {
    VnMessageService.markAsUnread(notificationId);
    setReadIds((prev) => prev.filter((id) => id !== notificationId));
  }, []);

  const refresh = useCallback(async () => {
    // Idle until OU is available (cleared storage / cold start). Do not thrash empty state.
    if (!ouId) {
      return { notifications: [], readIds: [] };
    }

    // Clear session deferrals when switching OUs so they do not carry across tenants.
    if (lastOuIdRef.current !== ouId) {
      deferredTakeoverIdsRef.current = new Set();
      lastOuIdRef.current = ouId;
    }

    setLoading(true);
    try {
      const all = await VnMessageService.listNotificationsByTarget(ouId);
      VnMessageService.cleanUpExpiredNotifications(all);
      const liveNotifications = all.filter((n) => isNotificationLiveBySendDate(n.sendDate));
      const read = VnMessageService.getRead();
      const unreadNotifications = liveNotifications.filter((n) => !read.includes(n.id));
      const deferredIds = deferredTakeoverIdsRef.current;
      const queue = getTakeoverQueue(unreadNotifications).filter((n) => !deferredIds.has(n.id));
      const hydratedQueue = await VnMessageService.populateNotificationBody(queue);

      setReadIds(read);
      setNotifications(liveNotifications);
      setTakeoverQueue(hydratedQueue);
      return { notifications: liveNotifications, readIds: read };
    } catch {
      setReadIds([]);
      setNotifications([]);
      setTakeoverQueue([]);
      return { notifications: [], readIds: [] };
    } finally {
      setLoading(false);
    }
  }, [ouId]);

  const value = useMemo<NotificationCenterContextValue>(
    () => ({
      notifications,
      unreadCount,
      readIds,
      loading,
      refresh,
      markAsRead,
      markAsUnread,
      preview: (notification: NotificationMessageDto) => {
        setPreview([notification]);
        setMode('preview');
      },
    }),
    [notifications, unreadCount, readIds, loading, refresh, markAsRead, markAsUnread]
  );

  useEffect(() => {
    if (!ouId) {
      return;
    }
    void refresh();
  }, [refresh, ouId]);

  const closePreview = useCallback(() => {
    setPreview([]);
    setMode('live');
  }, []);

  const onDismiss = useCallback(
    (notificationId: string) => {
      if (mode === 'preview') {
        closePreview();
        return;
      }

      markAsRead(notificationId);
      setTakeoverQueue((prev) => prev.filter((n) => n.id !== notificationId));
    },
    [mode, closePreview, markAsRead]
  );

  const onRemindLater = useCallback(
    (notificationId: string) => {
      if (mode === 'preview') {
        closePreview();
        return;
      }

      deferredTakeoverIdsRef.current.add(notificationId);
      setTakeoverQueue((prev) => prev.filter((n) => n.id !== notificationId));
    },
    [mode, closePreview]
  );

  const items = mode === 'preview' ? preview : takeoverQueue;
  const activeNotification = items[0];
  const activeNotificationId = activeNotification?.id;
  const isPreviewMode = mode === 'preview';

  const handleDismiss = useCallback(() => {
    if (!activeNotificationId) {
      return;
    }
    onDismiss(activeNotificationId);
  }, [activeNotificationId, onDismiss]);

  const handleRemindLater = useCallback(() => {
    if (!activeNotificationId) {
      return;
    }
    onRemindLater(activeNotificationId);
  }, [activeNotificationId, onRemindLater]);

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
      <ModalScaffold
        open={takeoverQueue.length > 0 || preview.length > 0}
        onBackdropClick={handleRemindLater}
        backdropAriaLabel={isPreviewMode ? 'Close preview' : 'Remind me later'}
      >
        <div className="notification-center__container">
          {activeNotification && (
            <>
              <div className="notification-center__stage">
                <NotificationDisplay
                  notification={activeNotification}
                  isPreviewMode={isPreviewMode}
                  onClosePreview={closePreview}
                  onRemindLater={handleRemindLater}
                  variant="takeover"
                />
              </div>
              <div className="notification-center__footer">
                <Button className="notification-center__footer-remind" onClick={handleRemindLater}>
                  Remind me later
                </Button>
                <Button
                  className="notification-center__footer-dismiss"
                  variant="contained"
                  color="primary"
                  onClick={handleDismiss}
                >
                  Dismiss
                </Button>
              </div>
            </>
          )}
        </div>
      </ModalScaffold>
    </NotificationCenterContext.Provider>
  );
};

function getTakeoverQueue(unreadNotifications: NotificationMessageDto[]): NotificationMessageDto[] {
  return unreadNotifications.filter((n) => n.showAsFullScreen);
}

export default NotificationCenterProvider;
